import {CurrencyPipe} from '@angular/common';
import {Component, Inject} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {SupportedLanguage} from '@emma-labs/shared/angular';
import {ENVIRONMENT, EnvironmentBroker, InsuranceCompany} from '@emma-labs/shared/typescript';
import {UntilDestroy} from '@ngneat/until-destroy';
import {Button} from 'primeng/button';
import {DialogModule} from 'primeng/dialog';
import {DividerModule} from 'primeng/divider';
import {InputTextModule} from 'primeng/inputtext';
import {MainComponent} from '../main/main.component';
import {QuoteComparatorClientSectionComponent} from './client-section/client-section.component';
import {QuoteComparatorRecommendationsSectionComponent} from './recommendations-section/recommendations-section.component';

export interface ClientForm {
  firstName: FormControl<string>;
  language: FormControl<SupportedLanguage>;
}

export interface RecommendationForm {
  company: FormControl<InsuranceCompany>;
  products: FormArray<FormGroup<ProductForm>>;
  reason: FormControl<string>;
  isRecommended: FormControl<boolean>;
}

export interface ProductForm {
  type: FormControl<string>;
  amount: FormControl<number | null>;
  price: FormControl<number | null>;
  reason: FormControl<string>;
}

@UntilDestroy()
@Component({
  selector: 'em-quote-comparator-page',
  templateUrl: './quote-comparator.component.html',
  styleUrls: ['./quote-comparator.component.scss'],
  imports: [
    QuoteComparatorRecommendationsSectionComponent,
    Button,
    DialogModule,
    ReactiveFormsModule,
    CurrencyPipe,
    DividerModule,
    MainComponent,
    QuoteComparatorClientSectionComponent,
    InputTextModule,
  ],
})
export class QuoteComparatorComponent {
  dialog = false;
  protected SupportedLanguage = SupportedLanguage;
  protected Company = InsuranceCompany;
  readonly dev: boolean;
  readonly clientFormGroup: FormGroup<ClientForm>;
  readonly recommendationsFormArray: FormArray<FormGroup<RecommendationForm>>;

  constructor(
    private readonly fb: FormBuilder,
    @Inject(ENVIRONMENT) private readonly environment: EnvironmentBroker,
  ) {
    this.dev = this.environment.showDevControls;

    this.clientFormGroup = this.createClientFormGroup();
    this.recommendationsFormArray = this.createRecommendationsFormArray();
  }

  getReasonsAsArray(recommendationIndex: number, productIndex: number): string[] {
    const reason =
      this.recommendationsFormArray.controls[recommendationIndex].controls.products.controls[productIndex]
        .controls.reason.value;
    return reason.split('\n').filter((r) => r !== '');
  }

  addRecommendation(): void {
    this.recommendationsFormArray.push(this.createRecommendationsFormGroup());
  }

  addProduct(index: number): void {
    this.recommendationsFormArray.controls[index].controls.products.push(this.createProductFormGroup());
  }

  deleteRecommandation(recommendationsFormIndex: number) {
    this.recommendationsFormArray.controls.splice(recommendationsFormIndex, 1);

    this.recommendationsFormArray.controls.forEach((recommendation) =>
      recommendation.updateValueAndValidity(),
    );
  }

  deleteProduct({
    recommendationsFormIndex,
    productFormIndex,
  }: {
    recommendationsFormIndex: number;
    productFormIndex: number;
  }) {
    this.recommendationsFormArray.controls[recommendationsFormIndex].controls.products.controls.splice(
      productFormIndex,
      1,
    );

    this.recommendationsFormArray.controls.forEach((recommendation) =>
      recommendation.controls.products.controls.forEach((product) => product.updateValueAndValidity()),
    );
  }

  simulate() {
    this.clientFormGroup.patchValue({
      firstName: 'Beet',
      language: SupportedLanguage.FR,
    });

    this.recommendationsFormArray.patchValue([
      {
        company: InsuranceCompany.BENEVA,
        reason: 'Because they the best whole life products considering that you have a high risk of dying',
        isRecommended: true,
        products: [
          {
            type: 'Whole Life T100',
            amount: 100000,
            price: 33.22,
            reason: 'This is the first reason\nAnd this is the second reason',
          },
          {type: 'Life', amount: 750000, price: 10.99, reason: ''},
        ],
      },
      {
        company: InsuranceCompany.EMMA,
        reason: 'Because Emma has the best price, product, devs, friends and they have MC Ismael',
        isRecommended: false,
        products: [
          {
            type: 'TRA',
            amount: 1000000,
            price: 99.99,
            reason: 'This is the first reason\nAnd this is the second reason',
          },
        ],
      },
      {
        company: InsuranceCompany.SUNLIFE,
        reason: 'Because sun is down, freezing cold, is in sicko mode',
        isRecommended: false,
        products: [{type: 'T10', amount: 400000, price: 42.39, reason: ''}],
      },
    ]);
  }

  private createClientFormGroup(): FormGroup<ClientForm> {
    return this.fb.group<ClientForm>({
      firstName: this.fb.control('', {nonNullable: true, validators: Validators.required}),
      language: this.fb.control(SupportedLanguage.FR, {nonNullable: true, validators: Validators.required}),
    });
  }

  private createRecommendationsFormArray(): FormArray<FormGroup<RecommendationForm>> {
    return this.fb.array<FormGroup<RecommendationForm>>([this.createRecommendationsFormGroup()]);
  }

  private createRecommendationsFormGroup(): FormGroup<RecommendationForm> {
    return this.fb.group<RecommendationForm>({
      company: this.fb.control(InsuranceCompany.NOTHING, {
        nonNullable: true,
        validators: Validators.required,
      }),
      reason: this.fb.control('', {nonNullable: true}),
      isRecommended: this.fb.control(false, {nonNullable: true, validators: Validators.required}),

      products: this.fb.array<FormGroup<ProductForm>>([this.createProductFormGroup()]),
    });
  }

  private createProductFormGroup(): FormGroup<ProductForm> {
    return this.fb.group<ProductForm>({
      type: this.fb.control('', {nonNullable: true, validators: Validators.required}),
      amount: this.fb.control(null, Validators.required),
      price: this.fb.control(null, Validators.required),
      reason: this.fb.control('', {nonNullable: true}),
    });
  }
}
